import { App } from '@capacitor/app';
import { Geolocation } from '@capacitor/geolocation';
import { useState, useEffect, useCallback } from 'react';

import { getPlatform, isNativePlatform } from 'src/utils/capacitor';

type PermissionStatus = 'prompt' | 'granted' | 'denied';

export function useGpsPermission() {
  const [permissionStatus, setPermissionStatus] = useState<PermissionStatus>('prompt');
  const [isGpsLoading, setIsGpsLoading] = useState(false);

  const isNative = isNativePlatform();
  const platform = getPlatform();

  // Check permission status
  const checkPermission = useCallback(async () => {
    if (isNative) {
      try {
        const status = await Geolocation.checkPermissions();
        const locationStatus = status.location;
        if (locationStatus === 'granted') {
          setPermissionStatus('granted');
        } else if (locationStatus === 'denied') {
          setPermissionStatus('denied');
        } else {
          setPermissionStatus('prompt');
        }
      } catch (error) {
        console.error('Error checking GPS permission:', error);
        setPermissionStatus('prompt');
      }
    } else {
      // Web
      try {
        const result = await navigator.permissions.query({ name: 'geolocation' });
        setPermissionStatus(
          result.state === 'prompt' ? 'prompt' : (result.state as PermissionStatus)
        );

        // Listen for changes
        result.addEventListener('change', () => {
          setPermissionStatus(
            result.state === 'prompt' ? 'prompt' : (result.state as PermissionStatus)
          );
        });
      } catch {
        // Firefox doesn't support permissions.query for geolocation
        setPermissionStatus('prompt');
      }
    }
  }, [isNative]);

  // Request permission
  const requestPermission = useCallback(async (): Promise<boolean> => {
    if (isNative) {
      try {
        const result = await Geolocation.requestPermissions();
        const granted = result.location === 'granted';
        setPermissionStatus(granted ? 'granted' : 'denied');
        return granted;
      } catch (error) {
        console.error('Error requesting GPS permission:', error);
        setPermissionStatus('denied');
        return false;
      }
    }
    // Web: permissions are requested when calling getCurrentPosition
    return true;
  }, [isNative]);

  // Open app settings (for when permission is denied)
  const openAppSettings = useCallback(async () => {
    if (!isNative) return;

    try {
      const { NativeSettings, AndroidSettings, IOSSettings } =
        await import('capacitor-native-settings');

      await NativeSettings.open({
        optionAndroid: AndroidSettings.ApplicationDetails,
        optionIOS: IOSSettings.App,
      });
    } catch (error) {
      console.error('Error opening app settings:', error);
    }
  }, [isNative]);

  // Check permission on mount
  useEffect(() => {
    checkPermission();
  }, [checkPermission]);

  // Re-check when app returns to foreground (native)
  useEffect(() => {
    if (!isNative) return undefined;

    const listener = App.addListener('appStateChange', ({ isActive }) => {
      if (isActive) {
        checkPermission();
      }
    });

    return () => {
      listener.then((l) => l.remove());
    };
  }, [isNative, checkPermission]);

  return {
    permissionStatus,
    isGpsLoading,
    isGpsDenied: permissionStatus === 'denied',
    isGpsGranted: permissionStatus === 'granted',
    isNative,
    platform,
    checkPermission,
    requestPermission,
    openAppSettings,
    setIsGpsLoading,
  };
}
